// Servicio para calcular el % de compatibilidad (fit) entre un comprador y una propiedad
// Este cálculo se ejecuta al guardar el perfil del comprador, NO al vuelo

import { supabase } from '../../supabaseClient';

/**
 * Criterios de comparación (SOLO los "required" cuentan):
 * - Habitaciones, Baños, Metros² → siempre son implícitamente required si están definidos
 * - Ascensor, Parking, Exterior → solo cuentan si están marcados como 'required'
 * - 'desirable' → NO cuenta para el cálculo (ni suma ni resta)
 * - 'not_needed' → NO se evalúa
 *
 * hasRequiredMismatch: true si hay al menos un criterio required que falla
 * (usado para mostrar color rojo aunque el % sea alto)
 */

// Labels para mostrar en el tooltip
const FIELD_LABELS = {
  bedrooms: 'Habitaciones',
  bathrooms: 'Baños',
  squareMeters: 'Metros²',
  elevator: 'Ascensor',
  parking: 'Parking',
  outdoorSpace: 'Exterior',
};

/**
 * Calcular el porcentaje de fit entre un perfil de comprador y los datos de una propiedad
 *
 * @param {Object} buyerProfile - Perfil del comprador (camelCase)
 * @param {Object} propertyMetadata - Metadata de la propiedad (del JSONB)
 * @returns {Object} { fitPercentage, hasRequiredMismatch, matchDetails }
 */
export const calculateFitPercentage = (buyerProfile, propertyMetadata) => {
  // Si no hay perfil o no hay metadata, no se puede calcular
  if (!buyerProfile || !propertyMetadata) {
    return { fitPercentage: null, hasRequiredMismatch: false, matchDetails: null };
  }

  const matches = [];
  const warnings = []; // Para casos como m² que están cerca pero no cumplen del todo
  const mismatches = [];
  let totalCriteria = 0;
  let matchedCriteria = 0;

  // ========== 1. HABITACIONES (minBedrooms) - siempre required ==========
  if (buyerProfile.minBedrooms && propertyMetadata.bedrooms !== undefined) {
    totalCriteria++;
    const hasEnoughBedrooms = propertyMetadata.bedrooms >= buyerProfile.minBedrooms;

    if (hasEnoughBedrooms) {
      matchedCriteria++;
      matches.push({
        field: 'bedrooms',
        label: FIELD_LABELS.bedrooms,
        buyer: `${buyerProfile.minBedrooms}+`,
        property: `${propertyMetadata.bedrooms}`,
        isRequired: true
      });
    } else {
      mismatches.push({
        field: 'bedrooms',
        label: FIELD_LABELS.bedrooms,
        buyer: `${buyerProfile.minBedrooms}+`,
        property: `${propertyMetadata.bedrooms}`,
        isRequired: true
      });
    }
  }

  // ========== 2. BAÑOS (minBathrooms) - siempre required ==========
  if (buyerProfile.minBathrooms && propertyMetadata.bathrooms !== undefined) {
    totalCriteria++;
    const hasEnoughBathrooms = propertyMetadata.bathrooms >= buyerProfile.minBathrooms;

    if (hasEnoughBathrooms) {
      matchedCriteria++;
      matches.push({
        field: 'bathrooms',
        label: FIELD_LABELS.bathrooms,
        buyer: `${buyerProfile.minBathrooms}+`,
        property: `${propertyMetadata.bathrooms}`,
        isRequired: true
      });
    } else {
      mismatches.push({
        field: 'bathrooms',
        label: FIELD_LABELS.bathrooms,
        buyer: `${buyerProfile.minBathrooms}+`,
        property: `${propertyMetadata.bathrooms}`,
        isRequired: true
      });
    }
  }

  // ========== 3. METROS CUADRADOS (desiredSqm) - con zona de advertencia ==========
  // Verde: >= desiredSqm
  // Ámbar (advertencia): entre 90% y 100% de desiredSqm
  // Rojo: < 90% de desiredSqm
  const propertySqm = propertyMetadata.builtArea ?? propertyMetadata.usableArea ?? propertyMetadata.squareMeters;

  if (buyerProfile.desiredSqm && propertySqm !== undefined) {
    totalCriteria++;
    const desiredSqm = buyerProfile.desiredSqm;
    const minAcceptable = desiredSqm * 0.9; // 90% del deseado

    if (propertySqm >= desiredSqm) {
      // Cumple completamente
      matchedCriteria++;
      matches.push({
        field: 'squareMeters',
        label: FIELD_LABELS.squareMeters,
        buyer: `${desiredSqm}+ m²`,
        property: `${propertySqm} m²`,
        isRequired: true
      });
    } else if (propertySqm >= minAcceptable) {
      // Zona de advertencia: está cerca (90-100%)
      matchedCriteria++; // Cuenta como "cumplido" para el % pero con advertencia
      warnings.push({
        field: 'squareMeters',
        label: FIELD_LABELS.squareMeters,
        buyer: `${desiredSqm}+ m²`,
        property: `${propertySqm} m²`,
        difference: desiredSqm - propertySqm,
        isRequired: true
      });
    } else {
      // No cumple (< 90%)
      mismatches.push({
        field: 'squareMeters',
        label: FIELD_LABELS.squareMeters,
        buyer: `${desiredSqm}+ m²`,
        property: `${propertySqm} m²`,
        isRequired: true
      });
    }
  }

  // ========== 4. ASCENSOR - solo cuenta si es 'required' ==========
  // 'desirable' y 'not_needed' no se evalúan
  if (buyerProfile.elevator === 'required' && propertyMetadata.elevator !== undefined) {
    totalCriteria++;
    const propertyHasElevator = propertyMetadata.elevator === true;

    if (propertyHasElevator) {
      matchedCriteria++;
      matches.push({
        field: 'elevator',
        label: FIELD_LABELS.elevator,
        buyer: 'Imprescindible',
        property: 'Sí',
        isRequired: true
      });
    } else {
      mismatches.push({
        field: 'elevator',
        label: FIELD_LABELS.elevator,
        buyer: 'Imprescindible',
        property: 'No',
        isRequired: true
      });
    }
  }

  // ========== 5. PARKING - solo cuenta si es 'required' ==========
  if (buyerProfile.parking === 'required' && propertyMetadata.parkingSpaces !== undefined) {
    totalCriteria++;
    const propertyHasParking = propertyMetadata.parkingSpaces > 0;

    if (propertyHasParking) {
      matchedCriteria++;
      matches.push({
        field: 'parking',
        label: FIELD_LABELS.parking,
        buyer: 'Imprescindible',
        property: `${propertyMetadata.parkingSpaces} plaza${propertyMetadata.parkingSpaces > 1 ? 's' : ''}`,
        isRequired: true
      });
    } else {
      mismatches.push({
        field: 'parking',
        label: FIELD_LABELS.parking,
        buyer: 'Imprescindible',
        property: 'No',
        isRequired: true
      });
    }
  }

  // ========== 6. ESPACIO EXTERIOR - solo cuenta si es 'required' ==========
  if (buyerProfile.outdoorSpace === 'required') {
    const outdoorValue = propertyMetadata.outdoorSpaces;
    const hasOutdoorData = outdoorValue !== undefined;

    if (hasOutdoorData) {
      totalCriteria++;

      // Determinar si tiene espacio exterior
      let propertyHasOutdoor = false;
      let outdoorDisplay = 'No';

      if (typeof outdoorValue === 'string') {
        const normalized = outdoorValue.toLowerCase().trim();
        propertyHasOutdoor = normalized !== 'no' && normalized !== '' && normalized !== 'sin exterior';
        outdoorDisplay = propertyHasOutdoor ? outdoorValue : 'No';
      } else if (typeof outdoorValue === 'boolean') {
        propertyHasOutdoor = outdoorValue;
        outdoorDisplay = outdoorValue ? 'Sí' : 'No';
      }

      if (propertyHasOutdoor) {
        matchedCriteria++;
        matches.push({
          field: 'outdoorSpace',
          label: FIELD_LABELS.outdoorSpace,
          buyer: 'Imprescindible',
          property: outdoorDisplay,
          isRequired: true
        });
      } else {
        mismatches.push({
          field: 'outdoorSpace',
          label: FIELD_LABELS.outdoorSpace,
          buyer: 'Imprescindible',
          property: 'No',
          isRequired: true
        });
      }
    }
  }

  // ========== CALCULAR PORCENTAJE ==========
  // Si no hay criterios evaluables, devolver null
  if (totalCriteria === 0) {
    return {
      fitPercentage: null,
      hasRequiredMismatch: false,
      hasWarnings: false,
      matchDetails: {
        matches: [],
        warnings: [],
        mismatches: [],
        totalCriteria: 0,
        matchedCriteria: 0,
        reason: 'No hay criterios comparables entre perfil y propiedad'
      }
    };
  }

  const fitPercentage = Math.round((matchedCriteria / totalCriteria) * 100);
  const hasRequiredMismatch = mismatches.length > 0;
  const hasWarnings = warnings.length > 0;

  return {
    fitPercentage,
    hasRequiredMismatch,
    hasWarnings,
    matchDetails: {
      matches,
      warnings,
      mismatches,
      totalCriteria,
      matchedCriteria,
    }
  };
};

/**
 * Obtener los datos de metadata de una propiedad
 *
 * @param {string} propertyId - ID de la propiedad
 * @returns {Object|null} Metadata de la propiedad o null
 */
export const getPropertyMetadata = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('property_metadata')
      .eq('id', propertyId)
      .single();

    if (error) throw error;

    // property_metadata es JSONB, puede ser string o ya objeto
    if (!data?.property_metadata) return null;

    // Si es string, parsear
    if (typeof data.property_metadata === 'string') {
      try {
        return JSON.parse(data.property_metadata);
      } catch {
        return null;
      }
    }

    return data.property_metadata;
  } catch (error) {
    console.error('Error obteniendo metadata de propiedad:', error);
    return null;
  }
};

/**
 * Calcular y guardar el fit_percentage para un perfil de comprador
 * Esta función se llama al crear/actualizar un perfil
 *
 * @param {string} profileId - ID del perfil de comprador
 * @param {Object} buyerProfile - Datos del perfil (camelCase)
 * @param {string} propertyId - ID de la propiedad
 * @returns {Object} { fitPercentage, hasRequiredMismatch, matchDetails }
 */
export const calculateAndSaveFit = async (profileId, buyerProfile, propertyId) => {
  try {
    // 1. Obtener metadata de la propiedad
    const propertyMetadata = await getPropertyMetadata(propertyId);

    // Debug: verificar datos
    console.log('🔍 Calculando fit:', {
      profileId,
      propertyId,
      buyerProfile: {
        minBedrooms: buyerProfile?.minBedrooms,
        minBathrooms: buyerProfile?.minBathrooms,
        desiredSqm: buyerProfile?.desiredSqm,
        elevator: buyerProfile?.elevator,
        parking: buyerProfile?.parking,
        outdoorSpace: buyerProfile?.outdoorSpace,
      },
      propertyMetadata: propertyMetadata ? {
        bedrooms: propertyMetadata.bedrooms,
        bathrooms: propertyMetadata.bathrooms,
        builtArea: propertyMetadata.builtArea,
        usableArea: propertyMetadata.usableArea,
        elevator: propertyMetadata.elevator,
        parkingSpaces: propertyMetadata.parkingSpaces,
        outdoorSpaces: propertyMetadata.outdoorSpaces,
      } : null,
    });

    // 2. Calcular fit
    const { fitPercentage, hasRequiredMismatch, matchDetails } = calculateFitPercentage(buyerProfile, propertyMetadata);

    // 3. Guardar en la BD (solo el porcentaje, el desglose se calcula al vuelo)
    const { error } = await supabase
      .from('buyer_profiles')
      .update({
        fit_percentage: fitPercentage,
      })
      .eq('id', profileId);

    if (error) throw error;

    console.log(`✅ Fit calculado para perfil ${profileId}: ${fitPercentage}%`,
      hasRequiredMismatch ? '⚠️ Con deal-breakers' : '✓ Sin deal-breakers',
      matchDetails);

    return { fitPercentage, hasRequiredMismatch, matchDetails };

  } catch (error) {
    console.error('Error calculando/guardando fit:', error);
    return { fitPercentage: null, hasRequiredMismatch: false, matchDetails: null };
  }
};

/**
 * Recalcular fit para todos los perfiles de una propiedad
 * Útil cuando se actualizan los datos de la propiedad
 *
 * @param {string} propertyId - ID de la propiedad
 * @returns {Object} { updated: number, failed: number }
 */
export const recalculateAllFitsForProperty = async (propertyId) => {
  try {
    // 1. Obtener metadata de la propiedad
    const propertyMetadata = await getPropertyMetadata(propertyId);

    if (!propertyMetadata) {
      console.log(`⚠️ No hay metadata para propiedad ${propertyId}, saltando recálculo`);
      return { updated: 0, failed: 0, skipped: true };
    }

    // 2. Obtener todos los perfiles de esta propiedad
    const { data: profiles, error: fetchError } = await supabase
      .from('buyer_profiles')
      .select('*')
      .eq('property_id', propertyId);

    if (fetchError) throw fetchError;

    if (!profiles || profiles.length === 0) {
      console.log(`📋 No hay perfiles para propiedad ${propertyId}`);
      return { updated: 0, failed: 0 };
    }

    // 3. Recalcular fit para cada perfil
    let updated = 0;
    let failed = 0;

    for (const profile of profiles) {
      try {
        // Mapear a camelCase para el cálculo
        const buyerProfile = {
          minBedrooms: profile.min_bedrooms,
          minBathrooms: profile.min_bathrooms,
          desiredSqm: profile.desired_sqm,
          elevator: profile.elevator,
          parking: profile.parking,
          outdoorSpace: profile.outdoor_space,
        };

        const { fitPercentage, hasRequiredMismatch, matchDetails } = calculateFitPercentage(buyerProfile, propertyMetadata);

        // Actualizar en BD (solo el porcentaje)
        const { error: updateError } = await supabase
          .from('buyer_profiles')
          .update({
            fit_percentage: fitPercentage,
          })
          .eq('id', profile.id);

        if (updateError) {
          console.error(`Error actualizando fit para perfil ${profile.id}:`, updateError);
          failed++;
        } else {
          updated++;
        }
      } catch (err) {
        console.error(`Error procesando perfil ${profile.id}:`, err);
        failed++;
      }
    }

    console.log(`✅ Recálculo de fits para ${propertyId}: ${updated} actualizados, ${failed} fallidos`);
    return { updated, failed };

  } catch (error) {
    console.error('Error en recálculo masivo de fits:', error);
    throw error;
  }
};

export default {
  calculateFitPercentage,
  getPropertyMetadata,
  calculateAndSaveFit,
  recalculateAllFitsForProperty,
};
